import { createModel, effect, signal } from '@preact/signals-react';
import type { MutationObserver, MutationObserverResult } from '@tanstack/query-core';
import type {
    NewWorkspace,
    UpdatedWorkspace,
    Workspace as LocalContractsWorkspace,
} from '@thinkalpha/ui-types/contracts/workspace';

export type SaveState = 'idle' | 'saving';
export type LoadState = 'idle' | 'loading';

export type CreateWorkspaceMutation = MutationObserver<LocalContractsWorkspace, Error, { workspace: NewWorkspace }>;

export type UpdateWorkspaceMutation = MutationObserver<LocalContractsWorkspace, Error, { workspace: UpdatedWorkspace }>;

export const WorkspaceOrchestrationModel = createModel(
    (init: { createWorkspaceMutation: CreateWorkspaceMutation; updateWorkspaceMutation: UpdateWorkspaceMutation }) => {
        const createResult = signal<
            MutationObserverResult<LocalContractsWorkspace, Error, { workspace: NewWorkspace }>
        >(init.createWorkspaceMutation.getCurrentResult());
        const updateResult = signal<
            MutationObserverResult<LocalContractsWorkspace, Error, { workspace: UpdatedWorkspace }>
        >(init.updateWorkspaceMutation.getCurrentResult());

        effect(() => {
            const unsubCreate = init.createWorkspaceMutation.subscribe((r) => (createResult.value = r));
            const unsubUpdate = init.updateWorkspaceMutation.subscribe((r) => (updateResult.value = r));
            return () => {
                unsubCreate();
                unsubUpdate();
            };
        });

        const saveState = signal<SaveState>('idle');
        const loadState = signal<LoadState>('idle');

        const saveAttempt = signal(0);
        const loadAttempt = signal(0);

        return {
            saveState,
            loadState,
            saveAttempt,
            loadAttempt,
            createResult,
            updateResult,

            /** Returns null when another save has already claimed the slot. */
            beginSave(): number | null {
                if (saveState.value === 'saving') return null;
                saveState.value = 'saving';
                saveAttempt.value = saveAttempt.value + 1;
                return saveAttempt.value;
            },

            endSave(attempt: number): void {
                if (saveAttempt.value === attempt) {
                    saveState.value = 'idle';
                }
            },

            isStaleSave(attempt: number): boolean {
                return saveAttempt.value !== attempt;
            },

            beginLoad(): number {
                loadAttempt.value = loadAttempt.value + 1;
                loadState.value = 'loading';
                return loadAttempt.value;
            },

            isStaleLoad(attempt: number): boolean {
                return loadAttempt.value !== attempt;
            },

            endLoad(attempt: number): void {
                if (loadAttempt.value === attempt) {
                    loadState.value = 'idle';
                }
            },
        };
    },
);

export type WorkspaceOrchestrationModel = InstanceType<typeof WorkspaceOrchestrationModel>;
